import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User as UserIcon, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { BRAND } from "../brand";

export default function Auth({ mode = "login" }) {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const r = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.email, form.password, form.name);
    setLoading(false);
    if (!r.ok) {
      setErr(r.error);
      toast.error(r.error);
      return;
    }
    toast.success(`${t("common.welcome_back")}, ${r.user.name}!`);
    navigate(next, { replace: true });
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]" data-testid={`${mode}-page`}>
      {/* Left cinematic banner */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="hidden lg:flex relative bg-[#0F0F14] overflow-hidden"
      >
        {/* Banner image */}
        <img
          src={BRAND.banner}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover scale-105"
          style={{ animation: "kenburns 30s ease-in-out infinite alternate" }}
        />
        {/* Dark + purple gradient overlay for readability */}
        <div className="absolute inset-0 bg-[#0F0F14]/55" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F0F14] via-[#0F0F14]/40 to-[#7E22CE]/30" />
        <div className="absolute -top-40 -right-32 w-[500px] h-[500px] rounded-full bg-[#A855F7]/30 blur-[120px] mix-blend-screen" />
        <div className="absolute bottom-0 -left-32 w-[400px] h-[400px] rounded-full bg-[#7E22CE]/30 blur-[100px] mix-blend-screen" />

        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/gameflow-logo-64.png" alt="" className="w-9 h-9 rounded-xl" />
            <span className="font-display text-xl font-bold">GAME<span className="text-[#C084FC]">FLOW</span></span>
          </Link>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs text-[#C084FC] mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Premium Anime · Gaming Marketplace
            </div>
            <h2 className="font-display text-4xl xl:text-5xl font-bold leading-tight drop-shadow-[0_4px_24px_rgba(168,85,247,0.4)]">
              <span className="text-gradient-purple">Level up</span><br />
              your digital world.
            </h2>
            <p className="text-slate-200/90 mt-4 max-w-sm drop-shadow">
              Join 2M+ gamers buying digital keys, gift cards and subscriptions with instant delivery.
            </p>
          </div>
          <div className="text-xs text-slate-300/70">© {new Date().getFullYear()} GAMEFLOW STORE</div>
        </div>
      </motion.div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900">
            {mode === "login" ? t("common.welcome_back") : t("common.create_account")}
          </h1>
          <p className="text-slate-500 mt-2">
            {mode === "login" ? t("common.no_account") : t("common.have_account")}{" "}
            <Link to={mode === "login" ? "/register" : "/login"} className="text-[#A855F7] font-semibold hover:underline">
              {mode === "login" ? t("common.sign_up") : t("common.sign_in")}
            </Link>
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid={`${mode}-form`}>
            {mode === "register" && (
              <Field icon={UserIcon} label={t("common.name")} type="text" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="name-input" />
            )}
            <Field icon={Mail} label={t("common.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="email-input" />
            <Field icon={Lock} label={t("common.password")} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required testid="password-input" />

            {err && <div className="text-sm text-red-500" data-testid="auth-error">{err}</div>}

            <Button type="submit" disabled={loading} className="btn-neon w-full h-12 rounded-xl font-semibold" data-testid={`${mode}-submit`}>
              {loading ? "…" : mode === "login" ? t("common.sign_in") : t("common.sign_up")}
            </Button>
          </form>

          <div className="mt-6 text-xs text-slate-500 text-center">
            By continuing you agree to our <a href="#" className="text-[#A855F7] hover:underline">Terms</a> and <a href="#" className="text-[#A855F7] hover:underline">Privacy</a>.
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, type, value, onChange, required, testid }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-700 mb-1.5 uppercase tracking-wider">{label}</div>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type={type}
          value={value}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-4 h-12 rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-[#A855F7] focus:ring-2 focus:ring-[#A855F7]/20 transition-all"
          data-testid={testid}
        />
      </div>
    </label>
  );
}
